import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CreditCard, Loader2, TriangleAlert } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import type {
  DevicePreferenceLocation,
  Membership,
  PutDevicePreferenceRequest,
} from "@tilt/pos-sdk/types";

import { getPosClient } from "../../lib/api";

/**
 * Per-location card-present default-device picker on the profile page
 * (V0053 / #155). The cashier sees one row per location they belong
 * to; choosing a terminal calls PUT /pos/v1/profile/device-preferences
 * which upserts ``pos_user_device_preference_tbl``. The MethodPicker
 * pre-selects this default whenever the cashier picks Card on a sale.
 *
 * Locations with zero card-present devices show an inline hint instead
 * of a select - the admin needs to sync devices on the Valor binding
 * before the cashier has anything to pin to. We still render the row
 * so the cashier sees that the location exists in their scope.
 */

export function DeviceDefaults({ activeMembership }: { activeMembership: Membership | null }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const prefs = useQuery({
    queryKey: ["device-preferences"],
    queryFn: () => getPosClient().getDevicePreferences(),
  });

  // Track in-flight mutations per location_id so concurrent saves
  // (cashier rapidly changes two locations) keep accurate spinner +
  // disabled state on each row independently. A single useMutation's
  // ``put.variables`` reflects only the LATEST call, so deriving
  // ``saving`` from it would mis-render the first row as idle while
  // its request is still in flight (Copilot review #50).
  const [pendingByLocation, setPendingByLocation] = useState<Record<string, true>>({});

  const put = useMutation({
    mutationFn: (body: PutDevicePreferenceRequest) => getPosClient().putDevicePreference(body),
    onMutate: (vars) => {
      setPendingByLocation((prev) => ({ ...prev, [vars.location_id]: true }));
    },
    onSettled: (_data, _err, vars) => {
      setPendingByLocation((prev) => {
        const { [vars.location_id]: _drop, ...rest } = prev;
        return rest;
      });
      void queryClient.invalidateQueries({ queryKey: ["device-preferences"] });
    },
  });

  if (prefs.isPending) {
    return (
      <section className="rounded-xl bg-surface p-5 shadow-elev-1 sm:p-6">
        <Header />
        <div className="mt-3 flex items-center gap-2 text-sm text-muted">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t("profile.devices.loading")}
        </div>
      </section>
    );
  }
  if (prefs.isError) {
    return (
      <section className="rounded-xl bg-surface p-5 shadow-elev-1 sm:p-6">
        <Header />
        <p className="mt-3 text-sm text-red-600 dark:text-red-400">
          {t("profile.devices.loadError")}
        </p>
      </section>
    );
  }

  const allItems = prefs.data?.items ?? [];
  // When location-scoped: show only the matching row (empty if the
  // location block is somehow absent rather than exposing other rows).
  // When org/partner-scoped: show all rows so the admin can set
  // defaults for each location they manage.
  const isLocationScope = activeMembership?.scope_kind === "location";
  const activeLocationId = activeMembership?.location?.id ?? null;
  const items = isLocationScope
    ? allItems.filter((it) => it.location_id === activeLocationId)
    : allItems;

  if (items.length === 0) {
    return (
      <section className="rounded-xl bg-surface p-5 shadow-elev-1 sm:p-6">
        <Header />
        <p className="mt-3 text-sm text-muted">{t("profile.devices.empty")}</p>
      </section>
    );
  }

  return (
    <section className="rounded-xl bg-surface p-5 shadow-elev-1 sm:p-6">
      <Header />
      <ul className="mt-4 space-y-3">
        {items.map((it) => (
          <LocationRow
            key={it.location_id}
            item={it}
            saving={pendingByLocation[it.location_id] === true}
            onChange={(deviceRef) =>
              put.mutate({ location_id: it.location_id, device_ref: deviceRef })
            }
          />
        ))}
      </ul>
      {put.isError && (
        <p className="mt-3 text-sm text-red-600 dark:text-red-400">
          {t("profile.devices.saveError")}
        </p>
      )}
    </section>
  );
}

function Header() {
  const { t } = useTranslation();
  return (
    <div>
      <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <CreditCard className="h-4 w-4" />
        {t("profile.devices.title")}
      </h2>
      <p className="mt-1 text-sm text-muted">{t("profile.devices.subtitle")}</p>
    </div>
  );
}

interface LocationRowProps {
  readonly item: DevicePreferenceLocation;
  readonly saving: boolean;
  readonly onChange: (deviceRef: string) => void;
}

function LocationRow({ item, saving, onChange }: LocationRowProps) {
  const { t } = useTranslation();
  const labelId = useId();
  const noDevices = item.available_devices.length === 0;

  // Guard against a stale saved default - admin can retire a Valor
  // device, leaving ``current_default_device_ref`` pointing at a
  // device that's no longer in ``available_devices``. Mirror the
  // MethodPicker guard: render the select with empty value + the
  // pick-placeholder option, and surface the same "no default yet"
  // hint so the cashier knows to repick (Copilot review #50).
  const savedRefValid =
    item.current_default_device_ref != null &&
    item.available_devices.some((d) => d.device_ref === item.current_default_device_ref);
  const selectValue = savedRefValid ? (item.current_default_device_ref ?? "") : "";

  return (
    <li className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-surface-muted px-4 py-3">
      <div className="min-w-0">
        <div
          id={labelId}
          className={
            noDevices
              ? "text-sm font-semibold text-foreground"
              : "text-sm font-medium text-foreground"
          }
        >
          {item.location_name}
        </div>
        {noDevices ? (
          <p className="mt-1 flex items-start gap-1 text-xs text-amber-700 dark:text-amber-400">
            <TriangleAlert className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
            {t("profile.devices.noDevicesAtLocation")}
          </p>
        ) : !savedRefValid ? (
          <p className="mt-1 text-xs text-muted">{t("profile.devices.noDefaultYet")}</p>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        {!noDevices && (
          <select
            aria-labelledby={labelId}
            value={selectValue}
            onChange={(e) => {
              const ref = e.target.value;
              if (ref) onChange(ref);
            }}
            disabled={saving}
            className="rounded-md bg-surface px-3 py-1.5 text-sm text-foreground ring-1 ring-inset ring-line focus:outline-none focus:ring-2 focus:ring-brand-primary disabled:opacity-60"
          >
            {!savedRefValid && <option value="">{t("profile.devices.pickPlaceholder")}</option>}
            {item.available_devices.map((d) => (
              <option key={d.device_ref} value={d.device_ref}>
                {d.label || d.device_ref}
              </option>
            ))}
          </select>
        )}
        {saving && <Loader2 className="h-4 w-4 animate-spin text-muted" />}
      </div>
    </li>
  );
}
